import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, ChevronUp, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../../components/ui/alert-dialog";
import api, { formatApiError } from "../../lib/api";

export default function Faqs() {
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ question: "", answer: "", is_active: true });
  const [deleteId, setDeleteId] = useState(null);
  const [saving, setSaving] = useState(false);

  const fetchFaqs = () => {
    api.get("/admin/faqs").then((r) => setFaqs(r.data)).catch((e) => toast.error(formatApiError(e))).finally(() => setLoading(false));
  };

  useEffect(fetchFaqs, []);

  const openCreate = () => {
    setEditing(null);
    setForm({ question: "", answer: "", is_active: true });
    setDialogOpen(true);
  };

  const openEdit = (faq) => {
    setEditing(faq);
    setForm({ question: faq.question, answer: faq.answer, is_active: faq.is_active });
    setDialogOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    if (saving) return;
    if (!form.question.trim() || !form.answer.trim()) {
      toast.error("Soru ve cevap alanları zorunludur");
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        await api.patch(`/admin/faqs/${editing.id}`, form);
        toast.success("Soru güncellendi");
      } else {
        await api.post("/admin/faqs", form);
        toast.success("Soru eklendi");
      }
      setDialogOpen(false);
      fetchFaqs();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    try {
      await api.delete(`/admin/faqs/${deleteId}`);
      toast.success("Soru silindi");
      setFaqs((prev) => prev.filter((f) => f.id !== deleteId));
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setDeleteId(null);
    }
  };

  const move = async (index, dir) => {
    const target = index + dir;
    if (target < 0 || target >= faqs.length) return;
    const reordered = [...faqs];
    [reordered[index], reordered[target]] = [reordered[target], reordered[index]];
    setFaqs(reordered);
    try {
      await api.post("/admin/faqs/reorder", { ids: reordered.map((f) => f.id) });
    } catch (e) {
      toast.error(formatApiError(e));
      fetchFaqs();
    }
  };

  const toggleActive = async (faq) => {
    try {
      await api.patch(`/admin/faqs/${faq.id}`, { is_active: !faq.is_active });
      setFaqs((prev) => prev.map((f) => (f.id === faq.id ? { ...f, is_active: !faq.is_active } : f)));
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const inputCls =
    "w-full bg-black/40 border border-white/10 rounded-xl px-4 py-3 text-sm text-white placeholder:text-[#64748B] outline-none focus:border-cyan-400/60 transition-colors";

  return (
    <div data-testid="admin-faqs-page">
      <div className="flex items-center justify-between mb-1">
        <h1 className="font-display font-700 text-xl text-white">Sıkça Sorulan Sorular</h1>
        <button onClick={openCreate} data-testid="faq-add-button" className="btn-glow inline-flex items-center gap-2 text-xs font-semibold text-white px-5 py-2.5 rounded-full">
          <Plus size={15} /> Yeni Soru
        </button>
      </div>
      <p className="text-sm text-[#64748B] mb-8">Soruları ekleyin, düzenleyin ve sıralayın</p>

      {loading ? (
        <p className="text-sm text-[#64748B]">Yükleniyor...</p>
      ) : faqs.length === 0 ? (
        <div data-testid="faqs-empty" className="rounded-2xl border border-dashed border-white/10 bg-[#0A0E17]/60 py-16 text-center">
          <p className="text-[#A0ABC0] text-sm">Henüz soru eklenmemiş.</p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="faqs-list">
          {faqs.map((faq, index) => (
            <div key={faq.id} data-testid={`faq-admin-row-${faq.id}`} className="flex items-center gap-4 rounded-2xl border border-white/[0.06] bg-[#0A0E17] p-4">
              <div className="flex flex-col gap-1 shrink-0">
                <button onClick={() => move(index, -1)} disabled={index === 0} data-testid={`faq-move-up-${faq.id}`} aria-label="Yukarı taşı"
                  className="text-[#64748B] hover:text-cyan-300 disabled:opacity-30 transition-colors">
                  <ChevronUp size={16} />
                </button>
                <button onClick={() => move(index, 1)} disabled={index === faqs.length - 1} data-testid={`faq-move-down-${faq.id}`} aria-label="Aşağı taşı"
                  className="text-[#64748B] hover:text-cyan-300 disabled:opacity-30 transition-colors">
                  <ChevronDown size={16} />
                </button>
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-white truncate">{faq.question}</p>
                <p className="text-xs text-[#64748B] truncate mt-1">{faq.answer}</p>
              </div>
              <button
                onClick={() => toggleActive(faq)}
                data-testid={`faq-active-toggle-${faq.id}`}
                className={`text-[11px] font-semibold border rounded-full px-3 py-1.5 transition-colors shrink-0 ${
                  faq.is_active
                    ? "text-emerald-300 bg-emerald-400/10 border-emerald-400/25"
                    : "text-[#64748B] bg-white/5 border-white/10"
                }`}
              >
                {faq.is_active ? "Aktif" : "Pasif"}
              </button>
              <div className="flex items-center gap-2 shrink-0">
                <button onClick={() => openEdit(faq)} data-testid={`faq-edit-${faq.id}`} aria-label="Düzenle"
                  className="w-8 h-8 rounded-lg border border-white/10 flex items-center justify-center text-[#A0ABC0] hover:text-cyan-300 hover:border-cyan-400/40 transition-colors">
                  <Pencil size={14} />
                </button>
                <button onClick={() => setDeleteId(faq.id)} data-testid={`faq-delete-${faq.id}`} aria-label="Sil"
                  className="w-8 h-8 rounded-lg border border-white/10 flex items-center justify-center text-[#A0ABC0] hover:text-red-300 hover:border-red-400/40 transition-colors">
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-[#0A0E17] border-white/10 text-white max-w-lg" data-testid="faq-form-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-base">{editing ? "Soruyu Düzenle" : "Yeni Soru Ekle"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={save} className="space-y-4" data-testid="faq-form">
            <div>
              <label className="block text-xs font-semibold text-[#A0ABC0] mb-2">Soru *</label>
              <input data-testid="faq-question-input" className={inputCls} value={form.question} onChange={(e) => setForm({ ...form, question: e.target.value })} placeholder="Soruyu yazın" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-[#A0ABC0] mb-2">Cevap *</label>
              <textarea data-testid="faq-answer-input" rows={4} className={`${inputCls} resize-none`} value={form.answer} onChange={(e) => setForm({ ...form, answer: e.target.value })} placeholder="Cevabı yazın" />
            </div>
            <label className="flex items-center gap-2.5 cursor-pointer">
              <input type="checkbox" data-testid="faq-active-checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} className="w-4 h-4 accent-cyan-400" />
              <span className="text-xs text-[#A0ABC0]">Sitede göster (Aktif)</span>
            </label>
            <button type="submit" disabled={saving} data-testid="faq-save-button" className="btn-glow w-full text-sm font-semibold text-white py-3.5 rounded-xl disabled:opacity-50">
              {saving ? "Kaydediliyor..." : editing ? "Değişiklikleri Kaydet" : "Soruyu Ekle"}
            </button>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent className="bg-[#0A0E17] border-white/10 text-white" data-testid="faq-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Soruyu Sil</AlertDialogTitle>
            <AlertDialogDescription className="text-[#A0ABC0]">Bu soruyu silmek istediğinize emin misiniz?</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="faq-delete-cancel" className="bg-transparent border-white/10 text-white hover:bg-white/5">Vazgeç</AlertDialogCancel>
            <AlertDialogAction data-testid="faq-delete-confirm" onClick={remove} className="bg-red-500 hover:bg-red-600 text-white">Sil</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
